/**
 * Locale translations: es (Spanish).
 * Keys and structure are shared across all locale files.
 */
export const es = {
  // Common
  common: {
    loading: "Cargando...",
    error: "Error",
    sort: "Ordenar",
    sortAsc: "Orden ascendente",
    sortDesc: "Orden descendente",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    remove: "Quitar",
    edit: "Editar",
    view: 'Ver',
    copy: "Copiar",
    create: "Crear",
    update: "Actualizar",
    search: "Buscar",
    actions: "Acciones",
    code: "Código",
    all: "Todo",
    clear: "Limpiar",
    filter: "Filtro",
    yes: "Sí",
    no: "No",
    confirm: "Confirmar",
    close: "Cerrar",
    select: "Seleccionar",
    active: "Activo",
    inactive: "Inactivo",
    success: "Éxito",
    updated: "Actualizado",
    created: "Creado",
    deleted: "Eliminado",
    confirmDelete: "¿Está seguro de que desea eliminar?",
    errorOccurred: "Ocurrió un error",
    noData: "No hay datos",
    status: "Estado",
    show: "Mostrar",
    hide: "Ocultar",
    searchPlaceholder: "Buscar...",
    allStatuses: "Todos los estados",
    clearFilters: "Limpiar filtros",
    newRecord: "Nuevo registro",
    noPermission: "No tienes permiso",
    statusDistribution: "Distribución de estados",
    rows: "filas",
    firstPage: "Primera página",
    previousPage: "Página anterior",
    nextPage: "Página siguiente",
    lastPage: "Última página",
    page: "Página",
    optional: "Opcional",
    saving: "Guardando...",
    deleteConfirmTitle: "Confirmación de eliminación",
    deleteWarning: "¡Esta acción no se puede deshacer!",
    autoTranslate: "Traducción automática",
    autoTranslateSuccessCount: "Se han traducido automáticamente {count} elementos.",
    autoTranslateNoSource: "No se encontró la traducción de origen.",
    enterTargetLanguage: "Por favor, elija el idioma de destino.",
    sourceLanguageLabel: "Idioma de origen (su interfaz)",
    targetLanguage: "Idioma de destino",
    sourceAndTargetMustDiffer: "El idioma de origen y el de destino deben ser diferentes.",
    context: "Contexto",
    key: "Clave",
    value: "Valor",
    locale: "Idioma",
    auto: "Auto",
    manual: "Manual",
    manageLocales: "Gestionar idiomas",
    activeLocalesDescription: "Elija los idiomas activos. Solo se guarda como ajuste.",
    updateUntranslatedFields: "Sincronizar nuevos campos",
    syncEntriesResult: "Sincronizadas {count} nuevas entradas de traducción.",
    syncDeletedResult: "Eliminadas {count} entradas obsoletas.",
    selectSourceLanguageForSync: "¿En qué idioma está el contenido?",
    sourceLanguage: "Idioma de origen",
    originalShort: "Orig.",
    languageNames: { en: "Inglés", tr: "Turco", de: "Alemán", fr: "Francés", es: "Español", ar: "Árabe", it: "Italiano", ja: "Japonés", zh: "Chino", ru: "Ruso" } as Record<string, string>,
    perPage: "Por página",
    darkMode: "Modo oscuro",
    lightMode: "Modo claro",
    copyright: "© {year} QubePro. Todos los derechos reservados.",
    translationRates: "Tasas de traducción",
    profileMenu: "Menú de perfil",
    manageYourAccount: "Gestionar cuenta",
    adminConsole: "Consola de administración",
    serverConfiguring: "Configurando el servidor, por favor espere...",
    productName: "Gestión",
  },

  portal: {
    searchPlaceholder: "Buscar aplicaciones...",
    noAppsFound: "No se encontraron aplicaciones.",
    quickLinks: "Enlaces rápidos",
    console: "Consola de administración",
    lastLogin: "Último inicio de sesión",
    apps: {
      core: { name: "Gestión", description: "Gestione empresas, organizaciones, usuarios, roles y permisos en una plataforma centralizada." },
      bi: { name: "Analítica", description: "Cree informes y paneles a partir de sus datos. Transforme los datos en información valiosa." },
      bpm: { name: "Procesos", description: "Inicie y complete solicitudes internas, aprobaciones y tareas digitalmente." },
      support: { name: "Soporte", description: "Gestione solicitudes de clientes y tickets. Rastree y resuelva problemas en un solo lugar." },
      agent: { name: "Automatización", description: "Automatice tareas rutinarias con bots y agentes. Se integra con sus herramientas." },
      ai: { name: "IA y Predicciones", description: "Use modelos de IA para análisis de datos y predicciones. Mejore la planificación." },
      erp: { name: "Suite de Negocios", description: "Módulos de finanzas, ventas, almacén y operaciones. Dirija su negocio en una sola plataforma." },
      pms: { name: "Proyectos", description: "Planifique proyectos, asigne tareas, rastree el progreso y colabore en equipo." },
      crm: { name: "Clientes", description: "Gestione contactos, prospectos, ofertas y embudos de ventas." },
    },
    menus: {
      core_dashboard: "Panel",
      core_companies: "Empresas",
      core_organizations: "Organizaciones",
      core_roles: "Roles",
      core_users: "Usuarios",
      core_groups: "Grupos",
      core_emails: "Cuentas de correo",
      core_data_sources: "Fuentes de datos",
      core_settings_company: "Ajustes de empresa",
      core_settings_user: "Ajustes de usuario",
      core_translations: "Traducciones",
    },
  },

  panel: {
    apps: {
      crm: { name: "CRM", footerName: "CRM" },
      accounting: { name: "Contabilidad" },
      finance: { name: "Finanzas" },
      procurement: { name: "Abastecimiento" },
      inventory: { name: "Inventario" },
      purchase: { name: "Compras" },
      sales: { name: "Ventas" },
      manufacturing: { name: "Fabricación" },
      assets: { name: "Activos" },
      hr: { name: "RR.HH." },
      project: { name: "Proyectos" },
      timesheet: { name: "Hojas de tiempo" },
      documents: { name: "Documentos" },
      pos: { name: "Punto de venta" },
      rental: { name: "Alquiler" },
      dashboard: { name: "Informes" },
    },
    appsLabel: "Aplicaciones",
    settingsLabel: "Ajustes",
    coreLabel: "Gestión",
    backToHome: "Volver al inicio",
  },

  errors: {
    unknown: "Ocurrió un error desconocido",
    network: "No se pudo conectar con el servidor. Revise su conexión.",
    timeout: "Tiempo de espera agotado. Inténtelo de nuevo.",
    badRequest: "Solicitud incorrecta",
    unauthorized: "Sesión expirada. Inicie sesión de nuevo.",
    forbidden: "No tiene permiso para esta acción.",
    notFound: "Recurso no encontrado",
    tooManyRequests: "Demasiadas solicitudes. Inténtelo más tarde.",
    internalServerError: "Error del servidor. Inténtelo más tarde.",
    badGateway: "Servidor temporalmente no disponible",
    serviceUnavailable: "Servicio temporalmente no disponible",
    gatewayTimeout: "El servidor no responde",
    serverErrorWithStatus: "Error del servidor ({status})",
  },

  // Dashboard
  dashboard: {
    title: "Panel de control",
    subtitle: "Resumen del sistema de gestión centralizado",
    welcome: "Bienvenido",
    guestName: "Invitado",
    statusSummary: "Resumen del estado actual",
    currentTimeLabel: "Hora actual",
    todayLabel: "Hoy",
    activeCompanies: "Empresas activas",
    activeOrganizations: "Organizaciones activas",
    activeUsers: "Usuarios activos",
    activeRoles: "Roles activos",
    database: "Base de datos",
    databaseUsed: "Espacio utilizado",
    recentlyAddedUsers: "Usuarios añadidos recientemente",
    recentlyAddedCompanies: "Empresas añadidas recientemente",
    noUsersYet: "No hay usuarios todavía",
    noCompaniesYet: "No hay empresas todavía",
    userPanel: "Panel de usuario",
    userPanelWelcome: "¡Bienvenido! Los ajustes de usuario están en el menú.",
    quickActions: "Acciones rápidas",
    systemHealthTitle: "Salud del sistema",
    systemLogsTitle: "Logs del sistema",
    logChartTitle: "Distribución de Errores/Advertencias",
    logErrorsLabel: "Errores",
    logWarningsLabel: "Advertencias",
    logSuccessLabel: "Éxito",
    statusHealthy: "Sano",
    statusFailed: "Fallido",
    statusUnknown: "Desconocido",
    lastRun: "Última ejecución",
    failures24h: "Fallos 24h",
    noData: "Sin datos",
    systemComponents: {
      EMAIL_INGEST: "Correo entrante (IMAP)",
      EMAIL_DISPATCH: "Correo saliente (SMTP)",
      JOB_SCHEDULER: "Planificador",
      API: "API",
    },
  },

  // Sidebar
  sidebar: {
    dashboard: "Panel",
    roles: "Roles",
    users: "Usuarios",
    settings: "Ajustes",
    systemSettings: "Ajustes del sistema",
    userSettings: "Ajustes de usuario",
    organizations: "Organizaciones",
    logout: "Cerrar sesión",
    toggleMenu: "Alternar menú",
    toggleMenuClose: "Cerrar menú",
    management: "Gestión",
    companies: "Empresas",
    emailAccounts: "Cuentas de correo",
    dataSources: "Fuentes de datos",
    translations: "Traducciones",
  },

  // Settings
  settings: {
    title: "Ajustes",
    subtitle: "Gestionar ajustes de empresa y usuario",
    companySettingsSubtitle: "Ajustes generales de la empresa",
    userSettingsSubtitle: "Sus ajustes personales",
    securitySettingsSubtitle: "Gestión de contraseña y seguridad",
    companySettings: "Ajustes de Empresa",
    userSettings: "Ajustes de Usuario",
    general: "General",
    personal: "Personal",
    timezone: "Zona horaria",
    dateFormat: "Formato de fecha",
    defaultCurrency: "Moneda por defecto",
    defaultLanguage: "Idioma por defecto",
    language: "Idioma",
    companySettingsSaved: "Ajustes de empresa guardados",
    userSettingsSaved: "Ajustes de usuario guardados",
    failedToLoad: "Error al cargar los ajustes",
    saveFailed: "Error al guardar los ajustes",
    profilePicture: "Foto de perfil",
    maxFileSize: "Máx 5MB. Formatos: JPEG, PNG, GIF, WebP",
    change: "Cambiar",
    uploading: "Subiendo...",
    deleteAvatar: "Eliminar foto",
    deleteAvatarConfirm: "¿Desea eliminar la foto de perfil?",
    avatarUploaded: "Foto subida con éxito",
    avatarDeleted: "Foto eliminada",
    avatarUploadError: "Error al subir la foto",
    avatarDeleteError: "Error al eliminar la foto",
    onlyImageFiles: "Solo se permiten imágenes",
    fileSizeError: "El archivo debe pesar menos de 5MB",
    appearance: "Apariencia",
    theme: "Tema",
    light: "Claro",
    dark: "Oscuro",
    pageSize: "Tamaño de página de tablas",
    defaultCompany: "Empresa por defecto",
    selectDefaultCompany: "Seleccione la empresa por defecto",
    noCompaniesAssigned: "No hay empresas asignadas",
    security: "Seguridad",
    changePassword: "Cambiar contraseña",
    currentPassword: "Contraseña actual",
    newPassword: "Nueva contraseña",
    confirmNewPassword: "Confirmar nueva contraseña",
    passwordChanged: "Contraseña cambiada con éxito",
    passwordChangeFailed: "Error al cambiar la contraseña",
    passwordMismatch: "Las nuevas contraseñas no coinciden",
    passwordTooShort: "La contraseña debe tener al menos 6 caracteres",
    passwordSameAsOld: "La nueva contraseña debe ser diferente a la actual",
    changingPassword: "Cambiando contraseña...",
    pleaseSelectCompany: "Por favor, seleccione una empresa",
  },

  // Organizations
  organizations: {
    title: "Organizaciones",
    code: "Código",
    name: "Nombre de la organización",
    description: "Descripción",
    logo: "Logo",
    logoEmpty: "Sin logo",
    uploadLogo: "Subir logo",
    status: "Estado",
    smtp: "SMTP",
    imap: "IMAP",
    smtpEmailAccount: "Cuenta de correo SMTP",
    imapEmailAccount: "Cuenta de correo IMAP",
    active: "Activa",
    inactive: "Inactiva",
    newOrganization: "Nueva organización",
    editOrganization: "Editar organización",
    deleteConfirm: "¿Desea eliminar esta organización?",
    deleteFailed: "Error al eliminar la organización",
    operationFailed: "Operación fallida",
    failedToLoad: "Error al cargar las organizaciones",
    dataFormatError: "Formato de datos inesperado",
    searchPlaceholder: "Buscar organización (nombre, descripción)",
    empty: "Sin datos",
    notSet: "Sin establecer",
    noCreatePermission: "No tienes permiso para crear",
    noUpdatePermission: "No tienes permiso para editar",
    noDeletePermission: "No tienes permiso para eliminar",
    company: "Nombre de la empresa",
    statusDistribution: "Distribución de estados de organizaciones",
  },

  // Users
  users: {
    title: "Usuarios",
    newUser: "Nuevo usuario",
    editUser: "Editar usuario",
    noUsersYet: "No hay usuarios todavía",
    failedToLoad: "Error al cargar los usuarios",
    deleteConfirm: "¿Desea eliminar este usuario?",
    deleteFailed: "Error al eliminar",
    operationFailed: "Operación fallida",
    passwordRequired: "La contraseña es obligatoria",
    passwordChangeHint: "(Llenar para modificar)",
    fullName: "Nombre completo",
    email: "Correo",
    status: "Estado",
    roles: "Roles",
    actions: "Acciones",
    firstName: "Nombre *",
    lastName: "Apellido *",
    password: "Contraseña",
    noRoles: "Sin roles",
    rolesNotFound: "No se encontraron roles",
    statusActive: "Activo",
    statusInactive: "Inactivo",
    statusSuspended: "Suspendido",
    userInfoFailed: "Error al cargar info de usuario",
    rolesFailed: "Error al cargar roles",
    copy: "Copiar",
    isAdmin: "Admin",
    groups: "Grupos",
    groupCount: "Nº grupos",
    userGroupName: "Nombre de grupo",
    userGroupCode: "Código de grupo",
    userGroupEmail: "Correo de grupo",
    groupManager: "Responsable de grupo",
    members: "Miembros",
    userGroupDescription: "Descripción",
    userGroupCreate: "Crear nuevo grupo",
    userGroupEdit: "Editar grupo",
    manageMembers: "Gestionar miembros",
    addMember: "Añadir miembro",
    memberCount: "Nº miembros",
    searchUsers: "Buscar usuarios...",
    noMembers: "Sin miembros todavía",
    selectUser: "Seleccionar usuario",
    addUser: "Añadir",
    removeUser: "Quitar",
    manageGroups: "Gestionar grupos",
    noUsersFound: "No se encontraron usuarios",
    statusDistribution: "Distribución de estados de usuarios",
    groupStatusDistribution: "Distribución de estados de grupos",
  },

  // Roles
  roles: {
    title: "Roles",
    newRole: "Nuevo rol",
    editRole: "Editar rol",
    noRolesYet: "No hay roles todavía",
    failedToLoad: "Error al cargar los roles",
    deleteConfirm: "¿Desea eliminar este rol?",
    deleteFailed: "Error al eliminar",
    operationFailed: "Operación fallida",
    dataFormatError: "Error de formato de datos",
    permissionsFailed: "Error al cargar los permisos",
    name: "Nombre del rol *",
    description: "Descripción",
    permissions: "Permisos",
    permissionCount: "Nº permisos",
    type: "Tipo de rol",
    typeSystem: "Sistema",
    typeCustom: "Personalizado",
    typeAll: "Todos los roles",
    searchPlaceholder: "Buscar (nombre, descripción, permiso)...",
    systemBadge: "Sistema",
    permissionsCount: "Permisos ({count})",
    morePermissions: "+ {count} otros",
    copy: "Copiar",
    copySuffix: " (copia)",
    clearFilters: "Limpiar filtros",
    noCreatePermission: "No tienes permiso para crear",
    noUpdatePermission: "No tienes permiso para editar",
    noDeletePermission: "No tienes permiso para eliminar",
    systemRoleEditBlocked: "Los roles de sistema no son editables",
    systemRoleDeleteBlocked: "Los roles de sistema no son eliminables",
    noPermissions: "Sin permisos",
    paginationRange: "{start}-{end} de {total}",
    paginationPage: "Página {page} / {total}",
    modules: {
      users: "Usuarios",
      groups: "Grupos",
      userGroupName: "Nombre de grupo",
      userGroupCode: "Código de grupo",
      groupManager: "Responsable de grupo",
      members: "Miembros",
      userGroupDescription: "Descripción",
      userGroupCreate: "Crear nuevo grupo",
      userGroupEdit: "Editar grupo",
      roles: "Roles",
      tenant: "Inquilino",
      userSettingField: "Ajuste",
      settings: "Ajustes",
      organizations: "Organizaciones",
    },
    groups: "Grupos",
    userGroup: "Grupo",
    userGroupList: "Lista de grupos",
    userGroupCreate: "Crear grupo",
    userGroupEdit: "Editar grupo",
    userGroupName: "Nombre de grupo",
    userGroupDescription: "Descripción",
    userGroupCode: "Código",
    groupManager: "Responsable de grupo",
    members: "Miembros",
    addMember: "Añadir miembro",
    assignRole: "Asignar rol",
    assign: "Asignar",
    statusDistribution: "Distribución de estados de roles",
    assignRoleMatrix: "Matriz de roles",
    roleMatrix: "Matriz de roles",
    selectCompanyToViewMatrix: "Seleccione una empresa para ver la matriz",
    show: "Mostrar",
    hide: "Ocultar",
    searchPermissions: "Buscar permisos...",
    permission: "Permiso",
    permissionsSelected: "{count} permisos seleccionados",
  },

  // Companies
  companies: {
    title: "Empresas",
    newCompany: "Nueva empresa",
    editCompany: "Editar empresa",
    noCompaniesYet: "No hay empresas todavía",
    failedToLoad: "Error al cargar las empresas",
    deleteConfirm: "¿Desea eliminar esta empresa?",
    deleteFailed: "Error al eliminar",
    operationFailed: "Operación fallida",
    dataFormatError: "Error de formato de datos",
    name: "Nombre de empresa *",
    status: "Estado",
    statusActive: "Activo",
    statusInactive: "Inactivo",
    searchPlaceholder: "Buscar (nombre, estado)...",
    clearFilters: "Limpiar filtros",
    copy: "Copiar",
    copySuffix: " (copia)",
    noCreatePermission: "No tienes permiso para crear",
    noUpdatePermission: "No tienes permiso para editar",
    noDeletePermission: "No tienes permiso para eliminar",
    paginationRange: "{start}-{end} de {total}",
    paginationPage: "Página {page} / {total}",
    allStatuses: "Todos los estados",
    statusDistribution: "Distribución de estados de empresas",
    show: "Mostrar",
    hide: "Ocultar",
    logo: "Logo",
    logoEmpty: "Sin logo",
    uploadLogo: "Subir logo",
    smtpEmailAccount: "Cuenta SMTP",
    imapEmailAccount: "Cuenta IMAP",
    smtpEmailId: "Cuenta SMTP",
    imapEmailId: "Cuenta IMAP",
  },

  // Emails
  emails: {
    title: "Cuentas de Correo",
    newEmail: "Nueva cuenta",
    editEmail: "Editar cuenta",
    noEmailsYet: "Sin cuentas disponibles",
    failedToLoad: "Error al cargar las cuentas",
    deleteConfirm: "¿Eliminar esta cuenta?",
    deleteConfirmMessage: "¿Desea eliminar la cuenta \"{name}\"?",
    deleteFailed: "Error al eliminar",
    operationFailed: "Operación fallida",
    dataFormatError: "Error de formato de datos",
    name: "Nombre de cuenta",
    type: "Tipo",
    host: "Host",
    port: "Puerto",
    user: "Usuario",
    password: "Contraseña",
    fromEmail: "Correo remitente",
    secure: "Seguro (SSL/TLS)",
    secureConnection: "Conexión segura (SSL/TLS)",
    status: "Estado",
    statusActive: "Activo",
    statusInactive: "Inactivo",
    activate: "Activar",
    deactivate: "Desactivar",
    isActive: "Activo",
    searchPlaceholder: "Buscar (nombre, host, usuario)...",
    clearFilters: "Limpiar filtros",
    copy: "Copiar",
    noCreatePermission: "No tienes permiso para crear",
    noUpdatePermission: "No tienes permiso para editar",
    noDeletePermission: "No tienes permiso para eliminar",
    paginationRange: "{start}-{end} de {total}",
    paginationPage: "Página {page} / {total}",
    allStatuses: "Todos los estados",
    allTypes: "Todos los tipos",
    statusDistribution: "Distribución de estados de cuentas",
    show: "Mostrar",
    hide: "Ocultar",
    nameRequired: "Nombre de cuenta obligatorio",
    hostRequired: "Host obligatorio",
    portRequired: "Puerto obligatorio",
    userRequired: "Usuario obligatorio",
    passwordRequired: "Contraseña obligatoria",
    leaveBlankToKeep: "Vacío para mantener actual",
    noData: "Sin datos",
    noEmailsMessage: "No se encontraron cuentas",
    passwordEditHint: "Vacío para no modificar",
    noSearchResults: "Sin resultados",
    namePlaceholder: "ej. Soporte o info@empresa.com",
    deleteConfirmTitle: "Eliminar cuenta",
  },

  // Login
  login: {
    title: "QubePro ERP",
    subtitle: "Sistema de gestión centralizado",
    heroTitle: "Gestión empresarial desde un solo lugar",
    heroSubtitle: "QubePro ERP unifica la gestión de empresas, organizaciones, usuarios y roles en una plataforma segura.",
    languageToggleLabel: "Idioma",
    turkish: "Turco",
    english: "Inglés",
    email: "Correo",
    password: "Contraseña",
    emailPlaceholder: "ejemplo@dominio.com",
    emailHint: "El inquilino se identifica automáticamente por el dominio",
    passwordPlaceholder: "••••••••",
    loginButton: "Ingresar",
    loggingIn: "Ingresando...",
    loginFailed: "Fallo al ingresar",
    darkMode: "Modo oscuro",
    lightMode: "Modo claro",
    loginPageTitle: "Acceso",
    submitButton: "Ingresar",
    featureTitle: "Planificación de recursos empresariales",
    featureDescription: "Gestione relaciones con clientes, ventas, inventario y procesos financieros en una plataforma integrada.",
    featureCard1Title: "Gestión de clientes",
    featureCard1Description: "Realice un seguimiento de sus clientes, ventas potenciales y relaciones comerciales desde un lugar central.",
    featureCard2Title: "Ventas y pedidos",
    featureCard2Description: "Gestione fácilmente cotizaciones, pedidos y procesos de venta para impulsar los ingresos.",
    featureCard3Title: "Informes y analíticas",
    featureCard3Description: "Monitoree el rendimiento empresarial con informes en tiempo real y tome decisiones basadas en datos.",
    featureCard4Title: "Integración y configuración",
    featureCard4Description: "Personalice su sistema, integre con otras herramientas y automatice sus procesos de negocio.",
    pleaseWait: "Por favor, espere",
    serverConfiguring: "Configurando el servidor, por favor espere...",
    dbCreating: "Creando base de datos y configuraciones. Puede tardar unos segundos.",
    emailLabel: "Correo",
    passwordLabel: "Contraseña",
    emailPlaceholderFull: "Ingrese su correo",
    passwordPlaceholderFull: "Ingrese su contraseña",
    showPassword: "Ver",
    hidePassword: "Ocultar",
    emailRequired: "El correo es obligatorio",
    passwordRequired: "La contraseña es obligatoria",
    backendUnavailable: "Servidor no disponible. Reintente más tarde.",
    backendConnectFailed: "Error de conexión. Reintente más tarde.",
    backendConnectFailedDev: "Error de conexión. Asegúrese que el backend corre ({apiUrl}).",
    fieldEmail: "Correo",
    fieldPassword: "Contraseña",
    errors: {
      invalidCredentials: "Correo o contraseña incorrectos.",
      databaseUnavailable: "Base de datos no disponible o configuración requerida. Reingrese.",
      accountInactive: "Su cuenta no está activa. Contacte al administrador.",
      sessionExpired: "Sesión expirada. Inicie sesión de nuevo.",
      tooManyAttempts: "Demasiados intentos. Reintente más tarde.",
      sessionTooLarge: "Datos de sesión muy pesados. Inténtelo de nuevo.",
    },
  },

  // DataSources
  dataSources: {
    title: "Fuentes de Datos",
    subtitle: "Gestionar conexiones a bases de datos de clientes",
    newDataSource: "Nueva fuente de datos",
    editDataSource: "Editar fuente de datos",
    noDataSourcesYet: "Sin fuentes disponibles",
    failedToLoad: "Error al cargar las fuentes",
    deleteConfirm: "¿Eliminar esta fuente de datos?",
    deleteFailed: "Error al eliminar",
    operationFailed: "Operación fallida",
    dataFormatError: "Error de formato de datos",
    name: "Nombre mostrado",
    driver: "Controlador",
    server: "Servidor",
    port: "Puerto",
    databaseName: "Nombre de BD",
    username: "Usuario",
    password: "Contraseña",
    schema: "Esquema",
    sourceType: "Tipo de origen",
    source: "Origen",
    target: "Destino",
    sourceSystem: "Sistema origen",
    targetSystem: "Sistema destino",
    status: "Estado",
    statusActive: "Activo",
    statusInactive: "Inactivo",
    searchPlaceholder: "Buscar (nombre, servidor, BD)...",
    clearFilters: "Limpiar filtros",
    copy: "Copiar",
    paginationRange: "{start}-{end} / {total}",
    paginationPage: "Página {page} / {total}",
    allStatuses: "Todos los estados",
    allTypes: "Todos",
    statusDistribution: "Distribución de estados de fuentes",
    show: "Mostrar",
    hide: "Ocultar",
    nameRequired: "Nombre obligatorio",
    serverRequired: "Dirección de servidor obligatoria",
    databaseRequired: "Nombre de BD obligatorio",
    passwordRequired: "Contraseña obligatoria",
    testConnection: "Probar conexión",
    testing: "Probando...",
    testSuccess: "¡Conexión exitosa!",
    testFailed: "Error de conexión: ",
    integratedSecurity: "Seguridad integrada",
    ssl: "SSL",
    connectionTimeout: "Tiempo de espera (seg)",
    description: "Descripción",
    noCreatePermission: "No tienes permiso para crear",
    noUpdatePermission: "No tienes permiso para editar",
    noDeletePermission: "No tienes permiso para eliminar",
  },

  aiProviders: {
    title: "Proveedores de IA",
    subtitle: "Gestión de modelos e integraciones de IA",
    newProvider: "Nuevo proveedor de IA",
    editProvider: "Editar proveedor de IA",
    noProvidersYet: "Sin datos",
    failedToLoad: "Error al cargar proveedores de IA",
    deleteConfirm: "¿Seguro que desea eliminar este proveedor de IA?",
    deleteFailed: "Error al eliminar proveedor de IA",
    operationFailed: "Operación fallida",
    name: "Nombre del proveedor *",
    provider: "Tipo de proveedor",
    baseUrl: "URL base",
    apiKey: "Clave API",
    description: "Descripción",
    status: "Estado",
    statusActive: "Activo",
    statusInactive: "Inactivo",
    searchPlaceholder: "Buscar (nombre, tipo, URL)...",
    clearFilters: "Limpiar filtros",
    noCreatePermission: "No tiene permiso para crear proveedores",
    statusDistribution: "Distribución de estados de proveedores",
    baseUrlPlaceholder: "ej. http://localhost:11434, https://api.custom.ai/v1",
    baseUrlEmptyDefault: "Vacío para valores por defecto",
    apiKeyPlaceholder: "Ingrese clave API",
  },
};
